(function () {
    'use strict';

    angular
        .module('Lavpop-store.services')
        .service('ProductService', ProductService)

    /** @ngInject */
    function ProductService($q, HttpService, LOCAL_STORAGE_ITEMS) {
        let productsList = [];

        const ProductService = {
            // API Methods
            getAllByParameters: getAllByParameters,
            getProductsBySkus: getProductsBySkus,

            // Getters
            getProducts: getProducts,
            getProductsTotal: getProductsTotal,
            getProductsCount: getProductsCount,

            // Methods
            addProduct: addProduct,
            addProducts: addProducts,
            updateProductAmount: updateProductAmount,
            removeProduct: removeProduct,
            clearProducts: clearProducts,
            updateCartExpiration: updateCartExpiration,
            cartIsValid: cartIsValid,
            isNetSuiteProduct: isNetSuiteProduct,

            // Configuration
            configureProducts: configureProducts,
        };

        return ProductService;

        // API Methods
        function getAllByParameters(params) {
            return HttpService.get('/products', {
                params
            });
        }

        function getProductsBySkus(skus) {
            const requestedSkus = (skus || [])
                .map(normalizeSkuCode)
                .filter((sku, index, list) => sku.length > 0 && list.indexOf(sku) === index);
            const normalizedSkus = normalizeSkus(skus);

            if (!normalizedSkus.length) {
                return $q.resolve({
                    products: [],
                    unresolvedSkus: []
                });
            }

            return HttpService.post('/products/by-skus', {
                skus: normalizedSkus,
                isForStore: shouldUseStoreFilterOnSkuSearch()
            }).then(products => {
                const shouldFilterByProvider = shouldFilterProviderOnSkuSearch();
                const resolvedProducts = shouldFilterByProvider
                    ? (products || []).filter(hasRealProvider)
                    : (products || []);
                const resolvedSkuSet = new Set(
                    resolvedProducts
                        .map(product => normalizeSkuCode(product.Code || product.code || product.SKU || product.sku))
                        .filter(Boolean)
                );
                // The request carries each SKU with and without the LP hyphen (LP-X / LPX);
                // a requested SKU is only unresolved when none of its variants came back.
                const unresolvedSkus = requestedSkus.filter(sku => !expandSkuVariants(sku).some(variant => resolvedSkuSet.has(variant)));

                return {
                    products: resolvedProducts,
                    unresolvedSkus
                };
            });
        }

        // Getters
        function getProducts() {
            return productsList;
        }

        function getProductsTotal(products) {
            if (products) {
                return products.reduce((acc, cur) => {
                    return acc + cur.Value * cur.Amount;
                }, 0);
            }
        }

        function getProductsCount(products) {
            if (!products)
                products = productsList;

            return products.reduce((acc, cur) => {
                return acc + cur.Amount;
            }, 0)
        }

        // Methods
        function addProduct(product) {
            const selectedProductValue = getPreferredProductValue(product);

            if (!selectedProductValue) {
                return;
            }

            const provider = product.Provider || {};
            const productValue = selectedProductValue.Value != null ? selectedProductValue.Value : (product.Value || 0);
            const stockAvailable = selectedProductValue.StockAvailable != null ? selectedProductValue.StockAvailable : (product.StockAvailable || 0);

            let currentProduct = productsList.find(prod => (prod.Id == product.Id && prod.UnitId == product.UnitId));

            if (currentProduct)
                currentProduct.Amount += product.Amount;
            else {
                let addedProduct = {
                    Id: product.Id,
                    ProductValueId: selectedProductValue.Id,
                    Title: product.Title || product.Name || '',
                    Image: product.Image,
                    Value: productValue,
                    StockAvailable: stockAvailable,
                    Amount: product.Amount,
                    UnitId: product.UnitId,
                    Unit: product.Unit,
                    ProviderId: provider.Id || product.ProviderId,
                    Provider: provider
                };

                productsList.push(addedProduct);
            }

            saveProducts();
            updateCartExpiration();
        }

        function addProducts(products) {
            if (!products || !products.length) {
                return;
            }

            products.forEach(addProduct);
        }

        function updateProductAmount(unitId, providerId, productId, amount) {
            let productFoundList = productsList.find(prod => (prod.UnitId == unitId && prod.ProviderId == providerId && prod.Id == productId));

            if (productFoundList) {
                productFoundList.Amount = amount;

                saveProducts();
            }
        }

        function removeProduct(unitId, providerId, product) {
            let productFoundList = productsList.find(prod => (prod.UnitId == unitId && prod.ProviderId == providerId && prod.Id == product.Id));
            let productFoundListIndex = productsList.indexOf(productFoundList);

            if (productFoundListIndex >= 0) {
                productsList.splice(productFoundListIndex, 1);

                saveProducts();
            }
        }

        function clearProducts() {
            productsList.length = 0;

            localStorage.removeItem(LOCAL_STORAGE_ITEMS.USER_CART);
            localStorage.removeItem(LOCAL_STORAGE_ITEMS.USER_CART_EXPIRATION);
        }

        function updateCartExpiration() {
            const cartProductsExpiration = moment().add(1, 'days').format('YYYY-MM-DD HH:mm:ss');

            localStorage.setItem(LOCAL_STORAGE_ITEMS.USER_CART_EXPIRATION, cartProductsExpiration);
        }

        function cartIsValid() {
            const cartProductsExpiration = localStorage.getItem(LOCAL_STORAGE_ITEMS.USER_CART_EXPIRATION);

            return cartProductsExpiration && moment().diff(cartProductsExpiration, 'minutes') < 0;
        }

        // Configuration
        function configureProducts() {
            let defer = $q.defer();

            const storageCartProducts = localStorage.getItem(LOCAL_STORAGE_ITEMS.USER_CART);

            if (storageCartProducts) {
                const parsedProducts = JSON.parse(storageCartProducts);

                productsList = parsedProducts;
            }

            defer.resolve();

            return defer.promise;
        }

        // Internal
        function normalizeSkus(skus) {
            if (!skus || !skus.length) {
                return [];
            }

            const normalized = skus
                .map(normalizeSkuCode)
                .filter(item => item.length > 0);
            const expanded = normalized.reduce((acc, sku) => acc.concat(expandSkuVariants(sku)), []);

            return expanded.filter((item, index, list) => list.indexOf(item) === index);
        }

        function normalizeSkuCode(value) {
            let code = value && value.toString ? value.toString().trim().toUpperCase() : '';

            if (!code) {
                return '';
            }

            // Canonicalize LavPop prefixed SKUs to API catalog format.
            // Examples:
            // LP16.155.08 -> LP-16.155.08
            // LPQUI0004 -> LP-QUI0004
            if (/^LP(?=\d|QUI)/.test(code) && code.indexOf('LP-') !== 0) {
                code = code.replace(/^LP(?=\d|QUI)/, 'LP-');
            }

            // Keep original catalog SKU format to match API `/products/by-skus`.
            code = code.replace(/-+$/, '');

            return code;
        }

        function expandSkuVariants(sku) {
            if (!sku) {
                return [];
            }

            const variants = [sku];

            if (/^LP-/.test(sku)) {
                variants.push(sku.replace(/^LP-/, 'LP'));
            } else if (/^LP(?=\d|QUI)/.test(sku)) {
                variants.push(sku.replace(/^LP(?=\d|QUI)/, 'LP-'));
            }

            return variants;
        }

        // Fornecedor com estoque controlado pelo NetSuite (Drilav, LOSEC): o estoque é exibido e limita o carrinho.
        function isNetSuiteProduct(product) {
            return !!(product && product.Provider && product.Provider.IsNetSuite);
        }

        function getPreferredProductValue(product) {
            if (!product || !product.ProductValue || !product.ProductValue.length) {
                return null;
            }

            let selectedProductValue = product.ProductValue.find(value => value && value.Active && value.StockAvailable > 0);

            if (!selectedProductValue) {
                selectedProductValue = product.ProductValue.find(value => value && value.StockAvailable > 0);
            }

            return selectedProductValue || product.ProductValue[0] || null;
        }

        function hasRealProvider(product) {
            const provider = product && product.Provider;
            const providerId = Number(
                (provider && provider.Id) ||
                product.ProviderId ||
                product.providerId ||
                0
            );
            const providerTitleRaw = (typeof provider === 'string'
                ? provider
                : (provider && (provider.Title || provider.Name || provider.ProviderName))) ||
                product.ProviderTitle ||
                product.ProviderName ||
                '';
            const providerTitle = providerTitleRaw.toString().trim();

            if (!providerId && !providerTitle) {
                return false;
            }

            if (providerTitle && /^(compra\s*externa\.?|fornecedor\s*externo|sob\s*pedido)$/i.test(providerTitle)) {
                return false;
            }

            return providerId > 0 || providerTitle.length > 0;
        }

        function shouldFilterProviderOnSkuSearch() {
            const host = window && window.location && window.location.hostname
                ? window.location.hostname.toLowerCase()
                : '';

            // Local test environment: do not block SKU resolution by provider shape.
            return host !== 'central-local.lavpop.com.br';
        }

        function shouldUseStoreFilterOnSkuSearch() {
            const host = window && window.location && window.location.hostname
                ? window.location.hostname.toLowerCase()
                : '';

            // Local test environment: query full catalog to validate kit composition.
            return host !== 'central-local.lavpop.com.br';
        }

        function saveProducts() {
            const storageCart = JSON.stringify(productsList);

            localStorage.setItem(LOCAL_STORAGE_ITEMS.USER_CART, storageCart);
        }
    }
}());
